import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { X, FileText, Image as ImageIcon, FileSignature, BellRing, Loader2, TrendingUp, Banknote, Wrench, Award } from "lucide-react";
import { adminLead, adminMediaUrl, adminGenerateAgreement, adminSendReminder, adminGenerateStatement, adminAddPayment, adminDeletePayment, adminSetService, adminIssueCertificate, formatApiErrorDetail, getToken, zar } from "@/api";

const Row = ({ label, value }) =>
  value !== undefined && value !== null && value !== "" ? (
    <div className="flex justify-between gap-6 py-2 border-b border-white/5 text-sm">
      <span className="text-slate-500 shrink-0">{label}</span>
      <span className="text-white text-right break-words">{String(value)}</span>
    </div>
  ) : null;

const Section = ({ title, children }) => (
  <div className="mb-6">
    <p className="text-[10px] uppercase tracking-[0.2em] text-sky-300 font-bold mb-2">{title}</p>
    {children}
  </div>
);

const DOC_LABELS = { id_copy: "ID copy", licence_copy: "Driver's licence", proof_of_income: "Proof of income" };

export default function LeadDetailModal({ leadId, onClose }) {
  const [lead, setLead] = useState(null);
  const [actionBusy, setActionBusy] = useState("");

  const genAgreement = async () => {
    if (!lead) return;
    setActionBusy("agreement");
    try {
      const res = await adminGenerateAgreement(lead.id);
      const link = `${window.location.origin}${res.path}`;
      try {
        await navigator.clipboard.writeText(link);
        toast.success("Agreement link copied — share it with the client");
      } catch {
        toast.success(`Agreement link: ${link}`);
      }
      setLead((l) => ({
        ...l,
        agreement: { ...(l.agreement || {}), token: res.token, sent_at: new Date().toISOString() },
        status: (l.agreement || {}).signed_at ? l.status : "agreement-sent",
      }));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setActionBusy("");
    }
  };

  const sendReminder = async () => {
    if (!lead) return;
    setActionBusy("reminder");
    try {
      await adminSendReminder(lead.id);
      toast.success(`Reminder emailed to ${lead.email}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setActionBusy("");
    }
  };

  const [stAlloc, setStAlloc] = useState("");
  const [stPayments, setStPayments] = useState("");

  useEffect(() => {
    if (lead?.payload?.vehicle?.max_monthly && !stAlloc) {
      setStAlloc(String(Math.round(Number(lead.payload.vehicle.max_monthly) * 0.5)));
    }
    if (lead?.statement) {
      setStAlloc(String(lead.statement.allocation_pm));
      setStPayments(String(lead.statement.payments_made));
    }
  }, [lead]);

  const genStatement = async () => {
    if (!lead) return;
    if (!stAlloc || Number(stAlloc) <= 0) return toast.error("Enter the monthly ownership allocation.");
    setActionBusy("statement");
    try {
      const res = await adminGenerateStatement(lead.id, {
        allocation_pm: Number(stAlloc),
        payments_made: Number(stPayments || 0),
      });
      const link = `${window.location.origin}${res.path}`;
      try {
        await navigator.clipboard.writeText(link);
      } catch { /* clipboard blocked */ }
      setLead((l) => ({
        ...l,
        statement: { token: res.token, allocation_pm: Number(stAlloc), payments_made: Number(stPayments || 0), generated_at: new Date().toISOString() },
      }));
      toast.success(res.emailed ? "Statement generated — link copied and emailed to client" : "Statement generated — link copied (email not delivered)");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setActionBusy("");
    }
  };

  const [payForm, setPayForm] = useState({ amount: "", paid_on: new Date().toISOString().slice(0, 10), note: "" });

  useEffect(() => {
    if (lead?.statement && !stAlloc) setStAlloc(String(lead.statement.allocation_pm));
    if ((lead?.payments || []).length && !stPayments) setStPayments(String(lead.payments.length));
  }, [lead]);

  const addPayment = async () => {
    if (!lead) return;
    if (!payForm.amount || Number(payForm.amount) <= 0) return toast.error("Enter the payment amount.");
    setActionBusy("payment");
    try {
      const payment = await adminAddPayment(lead.id, { ...payForm, amount: Number(payForm.amount) });
      const payments = [...(lead.payments || []), payment];
      setLead((l) => ({ ...l, payments }));
      setStPayments(String(payments.length));
      setPayForm({ amount: stAlloc || "", paid_on: new Date().toISOString().slice(0, 10), note: "" });
      toast.success("Payment logged");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setActionBusy("");
    }
  };

  const removePayment = async (paymentId) => {
    if (!lead) return;
    try {
      await adminDeletePayment(lead.id, paymentId);
      const payments = (lead.payments || []).filter((p) => p.id !== paymentId);
      setLead((l) => ({ ...l, payments }));
      setStPayments(String(payments.length));
      toast.success("Payment removed");
    } catch {
      toast.error("Could not remove payment");
    }
  };

  const [svc, setSvc] = useState({ last_service_date: "", interval_months: "6" });
  useEffect(() => {
    if (lead?.service) {
      setSvc({ last_service_date: lead.service.last_service_date || "", interval_months: String(lead.service.interval_months || 6) });
    }
  }, [lead?.id]);

  const saveService = async () => {
    if (!lead || !svc.last_service_date) return toast.error("Pick the last service date.");
    setActionBusy("service");
    try {
      const res = await adminSetService(lead.id, { last_service_date: svc.last_service_date, interval_months: Number(svc.interval_months) });
      setLead((l) => ({ ...l, service: { ...svc, interval_months: Number(svc.interval_months), next_due: res.next_due } }));
      toast.success(`Service schedule saved — next due ${res.next_due}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setActionBusy("");
    }
  };

  const issueCert = async () => {
    if (!lead) return;
    setActionBusy("certificate");
    try {
      const res = await adminIssueCertificate(lead.id);
      const link = `${window.location.origin}${res.path}`;
      try {
        await navigator.clipboard.writeText(link);
      } catch { /* clipboard blocked */ }
      setLead((l) => ({ ...l, certificate: { token: res.token, issued_at: new Date().toISOString() } }));
      toast.success("Certificate issued — link copied and emailed to the client");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setActionBusy("");
    }
  };

  useEffect(() => {
    adminLead(leadId).then(setLead).catch(() => toast.error("Could not load lead"));
  }, [leadId]);

  const openDoc = async (path) => {
    try {
      const res = await fetch(adminMediaUrl(path), { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), "_blank");
    } catch {
      toast.error("Could not open document");
    }
  };

  const p = lead?.payload || {};
  const docs = p.documents || {};

  return (
    <AnimatePresence>
      <motion.div
        data-testid="lead-detail-modal"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[70] bg-[#081738]/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-2xl bg-[#122659] border border-sky-300/20 rounded-xl p-8 my-8 max-h-[85vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="font-display text-2xl font-black text-white" data-testid="lead-detail-name">
                {lead ? lead.full_name || lead.name : "Loading…"}
              </h2>
              {lead?.reference && <p className="text-sm text-[#F57C00] font-bold mt-1">{lead.reference}</p>}
            </div>
            <button data-testid="lead-detail-close" onClick={onClose} className="text-slate-400 hover:text-white transition-colors" aria-label="Close">
              <X size={22} />
            </button>
          </div>

          {lead && (
            <>
              {lead.payment_due && (
                <div data-testid="lead-payment-overdue-banner" className="mb-6 bg-red-400/10 border border-red-400/40 rounded-lg px-4 py-3 text-sm text-red-300">
                  The debit-order date has passed and no payment is logged this month — follow up with the client.
                </div>
              )}
              <Section title="Contact">
                <Row label="Email" value={lead.email} />
                <Row label="Mobile" value={lead.mobile || lead.phone} />
                <Row label="Received" value={new Date(lead.created_at).toLocaleString("en-ZA")} />
                <Row label="Status" value={lead.status} />
              </Section>

              {lead.type === "application" && (
                <Section title="Agreement & follow-up">
                  <div className="flex flex-wrap gap-3 py-2">
                    <button
                      data-testid="lead-agreement-btn"
                      onClick={genAgreement}
                      disabled={actionBusy !== ""}
                      className="inline-flex items-center gap-2 bg-[#F57C00] hover:bg-[#E67300] disabled:opacity-60 text-white text-xs font-bold px-4 py-2.5 rounded-md transition-colors"
                    >
                      {actionBusy === "agreement" ? <Loader2 size={14} className="animate-spin" /> : <FileSignature size={14} />}
                      {lead.agreement?.token ? "Copy agreement link" : "Generate agreement link"}
                    </button>
                    <button
                      data-testid="lead-reminder-btn"
                      onClick={sendReminder}
                      disabled={actionBusy !== ""}
                      className="inline-flex items-center gap-2 border border-sky-300/30 text-sky-300 hover:bg-sky-300 hover:text-[#0B1F4E] disabled:opacity-60 text-xs font-bold px-4 py-2.5 rounded-md transition-colors"
                    >
                      {actionBusy === "reminder" ? <Loader2 size={14} className="animate-spin" /> : <BellRing size={14} />}
                      Send debit-order reminder
                    </button>
                    {(lead.settlement_paid || lead.status === "closed") && (
                      <button
                        data-testid="lead-certificate-btn"
                        onClick={issueCert}
                        disabled={actionBusy !== ""}
                        className="inline-flex items-center gap-2 border border-emerald-400/40 text-emerald-300 hover:bg-emerald-400 hover:text-[#0B1F4E] disabled:opacity-60 text-xs font-bold px-4 py-2.5 rounded-md transition-colors"
                      >
                        {actionBusy === "certificate" ? <Loader2 size={14} className="animate-spin" /> : <Award size={14} />}
                        {lead.certificate?.token ? "Copy certificate link" : "Issue ownership certificate"}
                      </button>
                    )}
                  </div>
                  {lead.agreement?.signed_at ? (
                    <p data-testid="lead-agreement-signed" className="text-xs text-emerald-300 mt-1">
                      Agreement signed by {lead.agreement.signed_name} on {new Date(lead.agreement.signed_at).toLocaleString("en-ZA")}
                    </p>
                  ) : lead.agreement?.token ? (
                    <p data-testid="lead-agreement-pending" className="text-xs text-slate-500 mt-1">
                      Agreement link generated — awaiting client signature.
                    </p>
                  ) : null}
                  {lead.agreement?.pdf_path && (
                    <button
                      data-testid="lead-agreement-pdf-btn"
                      onClick={() => openDoc(lead.agreement.pdf_path)}
                      className="inline-flex items-center gap-2 border border-emerald-400/40 text-emerald-300 hover:bg-emerald-400 hover:text-[#0B1F4E] text-xs font-bold px-4 py-2.5 rounded-md transition-colors mt-3"
                    >
                      <FileText size={14} /> Signed agreement PDF
                    </button>
                  )}
                </Section>
              )}

              {lead.type === "application" && (
                <Section title="Ownership statement">
                  <div className="grid grid-cols-2 gap-3 py-2">
                    <div>
                      <label className="input-label" htmlFor="ld-alloc">Allocation / month (R)</label>
                      <input id="ld-alloc" data-testid="statement-alloc-input" type="number" min="0" className="input-field" value={stAlloc} onChange={(e) => setStAlloc(e.target.value)} placeholder="3000" />
                    </div>
                    <div>
                      <label className="input-label" htmlFor="ld-payments">Payments received</label>
                      <input id="ld-payments" data-testid="statement-payments-input" type="number" min="0" className="input-field" value={stPayments} onChange={(e) => setStPayments(e.target.value)} placeholder="3" />
                    </div>
                  </div>
                  <button
                    data-testid="statement-generate-btn"
                    onClick={genStatement}
                    disabled={actionBusy !== ""}
                    className="inline-flex items-center gap-2 bg-[#F57C00] hover:bg-[#E67300] disabled:opacity-60 text-white text-xs font-bold px-4 py-2.5 rounded-md transition-colors"
                  >
                    {actionBusy === "statement" ? <Loader2 size={14} className="animate-spin" /> : <TrendingUp size={14} />}
                    {lead.statement?.token ? "Update & resend statement" : "Generate statement"}
                  </button>
                  {lead.statement?.token && (
                    <p data-testid="statement-existing" className="text-xs text-slate-500 mt-2">
                      Statement active — {lead.statement.payments_made} payments recorded at R {Number(lead.statement.allocation_pm).toLocaleString("en-ZA")}/month.
                    </p>
                  )}
                </Section>
              )}

              {lead.type === "application" && (
                <Section title={`Payments received (${(lead.payments || []).length})`}>
                  <div className="grid grid-cols-3 gap-3 py-2">
                    <div>
                      <label className="input-label" htmlFor="pay-amount">Amount (R)</label>
                      <input id="pay-amount" data-testid="payment-amount-input" type="number" min="0" className="input-field" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} placeholder={stAlloc || "3000"} />
                    </div>
                    <div>
                      <label className="input-label" htmlFor="pay-date">Date</label>
                      <input id="pay-date" data-testid="payment-date-input" type="date" className="input-field" value={payForm.paid_on} onChange={(e) => setPayForm({ ...payForm, paid_on: e.target.value })} />
                    </div>
                    <div className="flex items-end">
                      <button
                        data-testid="payment-add-btn"
                        onClick={addPayment}
                        disabled={actionBusy !== ""}
                        className="w-full inline-flex items-center justify-center gap-2 bg-[#F57C00] hover:bg-[#E67300] disabled:opacity-60 text-white text-xs font-bold px-4 py-3 rounded-md transition-colors"
                      >
                        {actionBusy === "payment" ? <Loader2 size={14} className="animate-spin" /> : <Banknote size={14} />}
                        Log payment
                      </button>
                    </div>
                  </div>
                  {(lead.payments || []).length > 0 ? (
                    <div className="mt-2 space-y-1.5" data-testid="payments-list">
                      {[...(lead.payments || [])].sort((a, b) => (b.paid_on || "").localeCompare(a.paid_on || "")).map((p) => (
                        <div key={p.id} data-testid={`payment-row-${p.id}`} className="flex items-center justify-between text-sm bg-[#0B1F4E] border border-sky-300/10 rounded-md px-4 py-2.5">
                          <span className="text-slate-400">{new Date(p.paid_on).toLocaleDateString("en-ZA", { day: "numeric", month: "short", year: "numeric" })}</span>
                          <span className="font-semibold text-white">{zar(p.amount)}</span>
                          <button
                            data-testid={`payment-delete-${p.id}`}
                            onClick={() => removePayment(p.id)}
                            className="text-slate-600 hover:text-red-400 transition-colors"
                            aria-label="Remove payment"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-500 mt-1">No payments logged yet. Logged payments automatically update the statement count.</p>
                  )}
                </Section>
              )}

              {lead.type === "application" && (
                <Section title="Service schedule">
                  <div className="grid grid-cols-2 gap-3 py-2">
                    <div>
                      <label className="input-label" htmlFor="svc-date">Last service date</label>
                      <input id="svc-date" data-testid="service-date-input" type="date" className="input-field" value={svc.last_service_date} onChange={(e) => setSvc({ ...svc, last_service_date: e.target.value })} />
                    </div>
                    <div>
                      <label className="input-label" htmlFor="svc-interval">Interval</label>
                      <select id="svc-interval" data-testid="service-interval-select" className="input-field" value={svc.interval_months} onChange={(e) => setSvc({ ...svc, interval_months: e.target.value })}>
                        <option value="3">Every 3 months</option>
                        <option value="6">Every 6 months</option>
                        <option value="12">Every 12 months</option>
                      </select>
                    </div>
                  </div>
                  <button
                    data-testid="service-save-btn"
                    onClick={saveService}
                    disabled={actionBusy !== ""}
                    className="inline-flex items-center gap-2 bg-[#F57C00] hover:bg-[#E67300] disabled:opacity-60 text-white text-xs font-bold px-4 py-2.5 rounded-md transition-colors"
                  >
                    {actionBusy === "service" ? <Loader2 size={14} className="animate-spin" /> : <Wrench size={14} />}
                    Save schedule
                  </button>
                  {lead.service?.next_due && (
                    <p data-testid="service-next-due" className="text-xs text-sky-300 mt-2">
                      Next service due {lead.service.next_due} — the client is emailed automatically 7 days before.
                    </p>
                  )}
                </Section>
              )}

              {lead.type === "prequalification" && (
                <Section title="Pre-qualification">
                  <Row label="Result" value={lead.result_status} />
                  <Row label="Employment" value={lead.employment_type} />
                  <Row label="Net income" value={zar(lead.net_income)} />
                  <Row label="Monthly expenses" value={zar(lead.monthly_expenses)} />
                  <Row label="Estimated surplus" value={zar(lead.surplus)} />
                  <Row label="Max affordable payment" value={zar(lead.max_affordable_payment)} />
                  <Row label="Deposit available" value={zar(lead.ownership_deposit)} />
                  <Row label="Preferred term" value={`${lead.preferred_term} months`} />
                  <Row label="Vehicle interest" value={lead.vehicle_interest} />
                </Section>
              )}

              {lead.type === "application" && (
                <>
                  <Section title="Vehicle & programme">
                    <Row label="Vehicle" value={[p.vehicle?.make, p.vehicle?.model].filter(Boolean).join(" ") || "Any"} />
                    <Row label="Year range" value={p.vehicle?.year_range} />
                    <Row label="Max price" value={p.vehicle?.max_price ? zar(p.vehicle.max_price) : ""} />
                    <Row label="Term" value={p.vehicle?.term ? `${p.vehicle.term} months` : ""} />
                    <Row label="Max monthly" value={p.vehicle?.max_monthly ? zar(p.vehicle.max_monthly) : ""} />
                    <Row label="Ownership deposit" value={p.vehicle?.ownership_deposit ? zar(p.vehicle.ownership_deposit) : ""} />
                    <Row label="Security deposit" value={p.vehicle?.security_deposit ? zar(p.vehicle.security_deposit) : ""} />
                    <Row label="Collection date" value={p.vehicle?.collection_date} />
                  </Section>
                  <Section title="Personal & driver">
                    <Row label="Full names" value={[p.personal?.first_names, p.personal?.surname].filter(Boolean).join(" ")} />
                    <Row label="ID / passport" value={p.personal?.id_number} />
                    <Row label="Date of birth" value={p.personal?.dob} />
                    <Row label="Address" value={p.personal?.address} />
                    <Row label="Licence no." value={p.personal?.licence_number} />
                    <Row label="Licence code" value={p.personal?.licence_code} />
                    <Row label="Licence expiry" value={p.personal?.licence_expiry} />
                    <Row label="Commute" value={p.personal?.commute_km ? `${p.personal.commute_km} km/day` : ""} />
                    <Row label="Parking" value={p.personal?.parking} />
                  </Section>
                  <Section title="Income & affordability">
                    <Row label="Employment" value={p.income?.employment_type} />
                    <Row label="Employer / business" value={p.income?.employer || p.income?.business_name} />
                    <Row label="Net salary / drawings" value={p.income?.net_salary ? zar(p.income.net_salary) : p.income?.monthly_drawings ? zar(p.income.monthly_drawings) : ""} />
                    <Row label="Other income" value={p.income?.other_income ? zar(p.income.other_income) : ""} />
                    <Row label="Total income" value={p.totals?.income ? zar(p.totals.income) : ""} />
                    <Row label="Total expenses" value={p.totals?.expenses ? zar(p.totals.expenses) : ""} />
                    <Row label="Surplus" value={p.totals ? zar(p.totals.surplus) : ""} />
                  </Section>
                  <Section title="Banking">
                    <Row label="Bank" value={p.banking?.bank_name} />
                    <Row label="Account holder" value={p.banking?.account_holder} />
                    <Row label="Account type" value={p.banking?.account_type} />
                    <Row label="Account number" value={p.banking?.account_number} />
                    <Row label="Branch code" value={p.banking?.branch_code} />
                    <Row label="Debit-order day" value={p.banking?.debit_day} />
                  </Section>
                  {Object.keys(docs).length > 0 && (
                    <Section title="Uploaded documents">
                      <div className="flex flex-wrap gap-3">
                        {Object.entries(docs).map(([key, path]) => (
                          <button
                            key={key}
                            data-testid={`lead-doc-${key}`}
                            onClick={() => openDoc(path)}
                            className="inline-flex items-center gap-2 border border-sky-300/30 text-sky-300 hover:bg-sky-300 hover:text-[#0B1F4E] text-xs font-bold px-4 py-2.5 rounded-md transition-colors"
                          >
                            {path.endsWith(".pdf") ? <FileText size={14} /> : <ImageIcon size={14} />}
                            {DOC_LABELS[key] || key}
                          </button>
                        ))}
                      </div>
                    </Section>
                  )}
                </>
              )}

              {lead.type === "rental_booking" && (
                <Section title="Rental booking">
                  <Row label="Reference" value={lead.reference} />
                  <Row label="Vehicle" value={lead.vehicle_name} />
                  <Row label="Collection" value={lead.start_date} />
                  <Row label="Return" value={lead.end_date} />
                  <Row label="Duration" value={lead.days ? `${lead.days} day${lead.days !== 1 ? "s" : ""}` : ""} />
                  <Row label="Day rate" value={lead.day_rate ? zar(lead.day_rate) : ""} />
                  <Row label="Estimated total" value={lead.estimated_total ? zar(lead.estimated_total) : "To quote"} />
                  <Row label="Deposit" value={lead.deposit_paid ? `Paid ${lead.deposit_paid_at ? new Date(lead.deposit_paid_at).toLocaleDateString("en-ZA") : ""}` : "Not paid yet"} />
                </Section>
              )}

              {lead.type === "trade_in" && (
                <Section title="Trade-in enquiry">
                  <Row label="Reference" value={lead.reference} />
                  <Row label="Their vehicle" value={lead.current_vehicle} />
                  <Row label="Mileage" value={lead.mileage_km ? `${Number(lead.mileage_km).toLocaleString("en-ZA")} km` : ""} />
                  <Row label="Condition" value={lead.condition} />
                  <Row label="Interested in" value={lead.desired_vehicle || "Any / advise"} />
                  <Row label="Notes" value={lead.message} />
                </Section>
              )}

              {lead.type === "contact" && (
                <Section title="Message">
                  <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-wrap">{lead.message}</p>
                </Section>
              )}
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
